import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { CORNER_ORIGINAL_KEYS, FIELD_MODULE_OWNERSHIP, getCustomFieldValue } from '../../utils/setupDataMapper';

// ── HELPERS ───────────────────────────────────────────────────────

const hasValue = (v) => v !== null && v !== undefined && v !== '';

/**
 * Riga compatta label | valore, a larghezza piena.
 * Ritorna null se il valore è vuoto (evita righe vuote).
 */
const FieldRow = ({ label, value, unit }) => {
  if (!hasValue(value)) return null;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center',
      justifyContent: 'space-between',
      py: '3px',
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-of-type': { borderBottom: 'none' },
    }}>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ mr: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
        {value}
        {unit && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {unit}
          </Typography>
        )}
      </Typography>
    </Box>
  );
};

/**
 * Griglia a 2 colonne per i campi — stessa riga per label e valore,
 * 2 campi affiancati per sfruttare la larghezza della card.
 */
const FieldGrid = ({ children }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0 12px',
  }}>
    {children}
  </Box>
);

/**
 * Header di sezione: uppercase, lettera spaziata, con linea sotto.
 */
const SectionLabel = ({ label }) => (
  <Typography variant="caption" sx={{
    display: 'block',
    pt: 1, pb: '3px',
    fontWeight: 700,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: 'text.disabled',
    borderBottom: '1px solid',
    borderColor: 'divider',
    mb: '2px',
  }}>
    {label}
  </Typography>
);

// ── CORNER CARD ───────────────────────────────────────────────────

/**
 * Visualizza i dati di un singolo angolo della vettura (FL/FR/RL/RR).
 *
 * Props:
 *   tag          – 'FL' | 'FR' | 'RL' | 'RR'
 *   data         – output di mapToCorners()[tag]
 *   setupData    – setupData raw (per custom fields)
 *   fieldConfig  – output di buildFieldConfig()
 *   t            – funzione di traduzione (useTranslation('setup'))
 */
const CornerCard = ({ tag, data, setupData, fieldConfig, t }) => {
  const { t: tTires } = useTranslation('tires');
  const { isHidden, isModuleActive, customFields } = fieldConfig;
  const keys = CORNER_ORIGINAL_KEYS[tag] || {};

  const show = (fieldName, value) => {
    const origKey = keys[fieldName];
    if (!origKey) return hasValue(value);
    const moduleKey = FIELD_MODULE_OWNERSHIP[origKey];
    if (moduleKey && !isModuleActive(moduleKey)) return false;
    if (isHidden(origKey)) return false;
    return hasValue(value);
  };

  const cornerLabel = {
    FL: t('quadrant_fl', { defaultValue: 'Ant. Sinistra' }),
    FR: t('quadrant_fr', { defaultValue: 'Ant. Destra' }),
    RL: t('quadrant_rl', { defaultValue: 'Post. Sinistra' }),
    RR: t('quadrant_rr', { defaultValue: 'Post. Destra' }),
  }[tag];

  const toeLabel = data?.toeDirection
    ? `${data.toe ?? ''} mm ${data.toeDirection}`
    : `${data?.toe ?? ''}`;

  const cornerCustomFields = customFields[tag] || [];

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5, py: 1,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography fontWeight={800} fontSize={17} color="text.primary" lineHeight={1}>
            {tag}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {cornerLabel}
          </Typography>
        </Box>
        {hasValue(data?.pressureCold) && (
          <Typography variant="body2" fontWeight={700}>
            {data.pressureCold}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>psi</Typography>
          </Typography>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ px: 1.5, pb: 1 }}>

        {/* ── GEOMETRIA ── */}
        {(show('toe', data?.toe) || show('camber', data?.camber) || show('caster', data?.caster) || show('rideHeight', data?.rideHeight)) && (
          <>
            <SectionLabel label={t('cc_section_geometry')} />
            <FieldGrid>
              {show('toe', data?.toe) && (
                <FieldRow label={t('cc_label_toe')} value={toeLabel} />
              )}
              {show('camber', data?.camber) && (
                <FieldRow label={t('cc_label_camber')} value={data.camber} unit="deg" />
              )}
              {show('caster', data?.caster) && (
                <FieldRow label={t('cc_label_caster')} value={data.caster} unit="deg" />
              )}
              {show('rideHeight', data?.rideHeight) && (
                <FieldRow label={t('cc_label_height')} value={data.rideHeight} unit="mm" />
              )}
            </FieldGrid>
          </>
        )}

        {/* ── MOLLA & BUMPSTOP ── */}
        {(show('spring', data?.spring) || show('bumpstopRate', data?.bumpstopRate) || show('bumpstopRange', data?.bumpstopRange)) && (
          <>
            <SectionLabel label={t('cc_section_springs')} />
            <FieldGrid>
              {show('spring', data?.spring) && (
                <FieldRow label={t('cc_label_spring')} value={data.spring} />
              )}
              {show('bumpstopRate', data?.bumpstopRate) && (
                <FieldRow label={t('cc_label_bs_rate')} value={data.bumpstopRate} />
              )}
              {show('bumpstopRange', data?.bumpstopRange) && (
                <FieldRow label={t('cc_label_bs_range')} value={data.bumpstopRange} unit="mm" />
              )}
            </FieldGrid>
          </>
        )}

        {/* ── AMMORTIZZATORI ── */}
        {(show('bumpSlow', data?.bumpSlow) || show('reboundSlow', data?.reboundSlow) ||
          show('bumpFast', data?.bumpFast) || show('reboundFast', data?.reboundFast)) && (
          <>
            <SectionLabel label={t('section_dampers')} />
            <FieldGrid>
              {show('bumpSlow', data?.bumpSlow) && (
                <FieldRow label={t('cc_label_bump_slow')} value={data.bumpSlow} unit="cl" />
              )}
              {show('reboundSlow', data?.reboundSlow) && (
                <FieldRow label={t('cc_label_rebound_slow')} value={data.reboundSlow} unit="cl" />
              )}
              {show('bumpFast', data?.bumpFast) && (
                <FieldRow label={t('cc_label_bump_fast')} value={data.bumpFast} unit="cl" />
              )}
              {show('reboundFast', data?.reboundFast) && (
                <FieldRow label={t('cc_label_rebound_fast')} value={data.reboundFast} unit="cl" />
              )}
            </FieldGrid>
          </>
        )}

        {/* ── GOMMA ── */}
        {(hasValue(data?.pressureCold) || hasValue(data?.pressureHot) || hasValue(data?.tireCode) || hasValue(data?.cornerWeight)) && (
          <>
            <SectionLabel label={t('cc_section_tyre')} />
            <FieldGrid>
              {hasValue(data?.pressureCold) && (
                <FieldRow label={t('cc_label_p_cold')} value={data.pressureCold} unit="psi" />
              )}
              {hasValue(data?.pressureHot) && (
                <FieldRow label={t('cc_label_p_hot')} value={data.pressureHot} unit="psi" />
              )}
              {hasValue(data?.tireCode) && (
                <FieldRow label={t('field_tire_code')} value={data.tireCode} />
              )}
              {hasValue(data?.cornerWeight) && (
                <FieldRow label={t('cc_corner_weight')} value={data.cornerWeight} unit="kg" />
              )}
            </FieldGrid>
            {/* Chip gomma + barra usura dal database live */}
            {(data?.tireBrand || data?.tireCompound || data?._liveTire) && (() => {
              const live = data._liveTire;
              const brand    = live?.brand    || data.tireBrand;
              const compound = live?.compound || data.tireCompound;
              // Calcolo usura: max tra km/lifeMaxKm e cycles/lifeMaxCycles
              const kmRatio    = (live?.lifeMaxKm     && live.kmTotal    != null) ? live.kmTotal    / live.lifeMaxKm     : null;
              const cycleRatio = (live?.lifeMaxCycles && live.heatCycles != null) ? live.heatCycles / live.lifeMaxCycles : null;
              const worst = Math.max(kmRatio || 0, cycleRatio || 0);
              const hasWear = kmRatio !== null || cycleRatio !== null;
              // Colore in base all'usura
              const wearColor = !hasWear ? '#999'
                : worst >= 0.8 ? '#d32f2f'   // rosso
                : worst >= 0.5 ? '#ed6c02'   // arancione
                : '#2e7d32';                 // verde
              const wearPct = Math.min(100, Math.round(worst * 100));
              return (
                <Box sx={{ mt: 0.5, mb: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color="primary"
                      label={`${brand || tTires('tire_chip_fallback_brand')}${compound ? ' · ' + compound : ''}`}
                      sx={{ fontSize: 10, height: 18 }}
                    />
                    {live && (
                      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
                        {live.kmTotal || 0} km · {live.heatCycles || 0} cicli
                      </Typography>
                    )}
                  </Box>
                  {hasWear && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                          {tTires('wear_label_short')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: wearColor }}>
                          {wearPct}%
                        </Typography>
                      </Box>
                      <Box sx={{ height: 4, bgcolor: 'rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ width: `${wearPct}%`, height: '100%', bgcolor: wearColor, transition: 'width .3s' }} />
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })()}
          </>
        )}

        {/* ── CUSTOM FIELDS ── */}
        {cornerCustomFields.length > 0 && (
          <>
            <SectionLabel
              label={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {t('subheader_custom_fields', { defaultValue: 'Custom' })}
                  <Chip
                    label="Template"
                    size="small"
                    sx={{ height: 14, fontSize: 9, fontWeight: 700, px: 0, bgcolor: 'success.main', color: '#fff', '& .MuiChip-label': { px: 0.6 } }}
                  />
                </Box>
              }
            />
            <FieldGrid>
              {cornerCustomFields.map((cf) => (
                <FieldRow
                  key={cf.key}
                  label={cf.label}
                  value={getCustomFieldValue(setupData, cf, tag)}
                />
              ))}
            </FieldGrid>
          </>
        )}

      </Box>
    </Paper>
  );
};

export default CornerCard;
